import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Coins, Check, Lock } from "lucide-react";
import { useGameStore, gameStore } from "@/hooks/useGameStore";
import { CROSSBOWS, ARROWS, SKINS, ASSETS, MINI_GAMES } from "@/data/catalog";

const UNLOCK_LABELS = {
  trophy_trial: { icon: "🏆", cta: "Win Trophy Trial", toast: "Win Trophy Trial to unlock this!" },
  boss:         { icon: "👹", cta: "Defeat Both Bosses", toast: "Defeat both bosses to unlock this!" },
};
function unlockLabel(condition) {
  if (UNLOCK_LABELS[condition]) return UNLOCK_LABELS[condition];
  const mg = MINI_GAMES.find((m) => m.id === condition);
  return { icon: mg?.icon || "🔒", cta: `Win ${mg?.name || condition}`, toast: `Win ${mg?.name || condition} to unlock this!` };
}
import { audio } from "@/game/audio";
import { toast } from "sonner";
import KeyedImage from "@/components/KeyedImage";

const CATEGORIES = [
  { key: "crossbow", label: "Crossbows", items: CROSSBOWS, hasImage: true },
  { key: "arrow",    label: "Arrows",    items: ARROWS,    hasImage: false },
  { key: "skin",     label: "Skins",     items: SKINS,     hasImage: true },
];

export default function ShopScreen() {
  const state = useGameStore();
  const nav = useNavigate();
  const { tab: tabParam } = useParams();
  const validTabs = ["crossbow", "arrow", "skin"];
  // Normalize: bare /shop or an unknown tab redirects to /shop/crossbow so
  // every shop URL is a tab URL (clean share links, separate Vercel Analytics
  // entries per tab).
  if (!tabParam || !validTabs.includes(tabParam)) {
    return <Navigate to="/shop/crossbow" replace />;
  }
  const tab = tabParam;
  const setTab = (key) => nav(`/shop/${key}`, { replace: true });

  const category = CATEGORIES.find((c) => c.key === tab);

  const handleAction = (item) => {
    const owned = state.owned[tab].includes(item.id);
    if (owned) {
      gameStore.equip(tab, item.id);
      audio.start();
      toast.success(`${item.name} equipped!`);
      return;
    }
    const conditionMet = item.unlockCondition
      ? (state.miniGamesWon[item.unlockCondition] || 0) > 0
      : true;
    if (!conditionMet) {
      audio.miss();
      toast.error(unlockLabel(item.unlockCondition).toast);
      return;
    }
    if (item.unlockCondition && conditionMet) {
      gameStore.claimItem(tab, item.id);
      gameStore.equip(tab, item.id);
      audio.buy();
      toast.success(`${item.name} claimed & equipped!`);
      return;
    }
    const ok = gameStore.buy(tab, item.id, item.price);
    if (!ok) {
      audio.miss();
      toast.error("Not enough coins!");
      return;
    }
    audio.buy();
    toast.success(`${item.name} unlocked & equipped!`);
  };

  return (
    <div className="relative w-full h-full arcade-bg overflow-auto" data-testid="shop-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-md bg-[#0B0C10]/60 border-b border-white/5 safe-top">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <button
            className="hud-pill hud-pill--cyan"
            onClick={() => nav("/")}
            data-testid="shop-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#00FFFF]" />
            <span className="text-[#00FFFF] font-display text-sm">Home</span>
          </button>
          <h1 className="font-display font-black text-2xl sm:text-3xl neon-pink">SHOP</h1>
          <div className="hud-pill hud-pill--gold">
            <Coins className="w-4 h-4 text-[#FFD700]" />
            <span className="neon-yellow text-base" data-testid="shop-coins">{state.coins.toLocaleString()}</span>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-6xl mx-auto flex gap-2 px-4 pb-3 overflow-x-auto">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setTab(c.key)}
              data-testid={`tab-${c.key}`}
              className={`px-5 py-2 rounded-full font-display text-sm uppercase tracking-wider whitespace-nowrap border-2 transition-all ${
                tab === c.key
                  ? "bg-[#FF007F] border-white/50 text-white shadow-[0_0_18px_#FF007F]"
                  : "bg-transparent border-white/20 text-white/70 hover:border-[#00FFFF] hover:text-[#00FFFF]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Items grid */}
      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {category.items.map((item) => {
            const conditionMet = item.unlockCondition
              ? (state.miniGamesWon[item.unlockCondition] || 0) > 0
              : true;
            return (
              <ShopItem
                key={item.id}
                item={item}
                category={tab}
                owned={state.owned[tab].includes(item.id)}
                equipped={state.equipped[tab] === item.id}
                canAfford={state.coins >= item.price}
                conditionLocked={!!item.unlockCondition && !conditionMet}
                conditionMet={!!item.unlockCondition && conditionMet}
                onAction={() => handleAction(item)}
                hasImage={category.hasImage}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ShopItem({ item, category, owned, equipped, canAfford, conditionLocked, conditionMet, onAction, hasImage }) {
  const src = hasImage ? ASSETS[item.id] : null;
  const showEmoji = hasImage && !src && item.emoji;
  return (
    <div
      className={`neon-card p-4 sm:p-5 flex flex-col items-center gap-3 ${equipped ? "neon-card--equipped" : ""} ${!owned && !canAfford && !conditionLocked && !conditionMet ? "neon-card--locked" : ""} ${conditionLocked ? "neon-card--locked" : ""}`}
      data-testid={`item-${item.id}`}
    >
      <div className="w-full aspect-square rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden relative">
        {src ? (
          <KeyedImage src={src} alt={item.name} className={`w-full h-full object-contain p-3 ${conditionLocked ? "opacity-40 grayscale" : ""}`} />
        ) : showEmoji ? (
          <div
            className={`text-6xl sm:text-7xl ${conditionLocked ? "opacity-40 grayscale" : ""}`}
            style={{ filter: "drop-shadow(0 0 14px #00FFFF)" }}
            aria-label={item.name}
          >
            {item.emoji}
          </div>
        ) : (
          <ArrowIcon id={item.id} color={item.color} />
        )}
        {equipped && (
          <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#39FF14] flex items-center justify-center shadow-[0_0_12px_#39FF14]">
            <Check className="w-4 h-4 text-black" strokeWidth={3} />
          </div>
        )}
        {conditionLocked && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-4xl drop-shadow-[0_0_12px_#FFD700]">{unlockLabel(item.unlockCondition).icon}</span>
          </div>
        )}
      </div>
      <div className="text-center w-full">
        <div className="font-display font-bold text-sm sm:text-base truncate">{item.name}</div>
        {owned ? (
          equipped ? (
            <div className="mt-2 text-xs font-display uppercase tracking-wider neon-green">Equipped</div>
          ) : (
            <button
              onClick={onAction}
              className="mt-2 btn-neon btn-cyan w-full text-xs py-2 px-3"
              data-testid={`equip-${item.id}`}
            >
              Equip
            </button>
          )
        ) : conditionLocked ? (
          <button
            onClick={onAction}
            className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-full py-2 px-3 text-xs font-display font-bold uppercase tracking-wider border-2 bg-[#FFD700]/10 text-[#FFD700]/60 border-[#FFD700]/30 cursor-not-allowed"
            data-testid={`locked-${item.id}`}
          >
            {unlockLabel(item.unlockCondition).icon} {unlockLabel(item.unlockCondition).cta}
          </button>
        ) : conditionMet ? (
          <button
            onClick={onAction}
            className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-full py-2 px-3 text-xs font-display font-bold uppercase tracking-wider border-2 bg-[#FFD700] text-black border-white/40 hover:shadow-[0_0_16px_#FFD700] active:scale-95"
            data-testid={`claim-${item.id}`}
          >
            {unlockLabel(item.unlockCondition).icon} Claim Reward
          </button>
        ) : (
          <button
            onClick={onAction}
            disabled={!canAfford}
            className={`mt-2 w-full inline-flex items-center justify-center gap-1 rounded-full py-2 px-3 text-xs font-display font-bold uppercase tracking-wider border-2 transition-all ${
              canAfford
                ? "bg-[#FFD700] text-black border-white/40 hover:shadow-[0_0_16px_#FFD700] active:scale-95"
                : "bg-white/5 text-white/60 border-white/20 cursor-not-allowed"
            }`}
            data-testid={`buy-${item.id}`}
          >
            {canAfford ? <Coins className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
            {item.price}
          </button>
        )}
      </div>
    </div>
  );
}

function ArrowIcon({ id, color }) {
  const rainbow = color === "rainbow";
  const c = rainbow ? "url(#rainbowGrad)" : color;
  return (
    <svg viewBox="0 0 120 40" className="w-4/5 h-4/5">
      <defs>
        <linearGradient id="rainbowGrad" x1="0" x2="1">
          <stop offset="0%" stopColor="#FF007F" />
          <stop offset="33%" stopColor="#FFD700" />
          <stop offset="66%" stopColor="#39FF14" />
          <stop offset="100%" stopColor="#00FFFF" />
        </linearGradient>
        <filter id="glowF" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g filter="url(#glowF)">
        <rect x="10" y="18" width="70" height="4" fill={c} rx="2" />
        <polygon points="80,10 100,20 80,30" fill={c} />
        <polygon points="10,20 2,12 2,28" fill="#FF007F" />
      </g>
    </svg>
  );
}
